// lib/desktop/google-oauth-desktop.ts
//
// Login con Google en la app de ESCRITORIO (Tauri, edición cloud).
//
// En la web, `signIn("google")` hace un full-page redirect a Google y vuelve por
// el callback de Convex. En el webview de Tauri ese redirect no vuelve, así que
// usamos el flujo "manual" de ConvexAuth (el mismo que RN):
//
//   1. `signIn("google", { redirectTo: DEEP_LINK })` → devuelve la URL de OAuth.
//   2. Abrimos esa URL en el NAVEGADOR DEL SISTEMA (openExternal).
//   3. Google → callback de Convex → Convex redirige al deep link
//      `heskala://auth/callback?code=...` (permitido en convex/auth.ts).
//   4. El SO abre la app con ese deep link; `listenForDesktopOAuthCallback` lo
//      captura, extrae `code` y llama `signIn("google", { code })` para cerrar la
//      sesión. El verifier PKCE lo guardó el paso 1 en este mismo webview.

import type { useAuthActions } from "@convex-dev/auth/react";
import { isDesktopApp } from "@/lib/edition/is-desktop";
import { openExternal } from "@/lib/desktop/open-external";

// Debe coincidir con el scheme registrado en Tauri (tauri.conf.json →
// plugins.deep-link.desktop.schemes) y con el allow-list de convex/auth.ts.
export const DESKTOP_OAUTH_REDIRECT = "heskala://auth/callback";

// Tipo exacto de `signIn` de ConvexAuth (evita desajustes con el firmado real).
type SignInFn = ReturnType<typeof useAuthActions>["signIn"];

/** Inicia el login con Google en desktop (abre el navegador del sistema). */
export async function startDesktopGoogleSignIn(signIn: SignInFn): Promise<void> {
  // ConvexAuth, tras pedir la URL de OAuth, NAVEGA el window a Google
  // (window.location.href = url) salvo que navigator.product === "ReactNative".
  // En el webview de Tauri eso abriría Google DENTRO de la app (conflicto),
  // además del navegador del sistema que abrimos nosotros. Spoofeamos
  // product="ReactNative" SOLO durante el signIn: así la librería no navega y
  // solo devuelve la URL (mismo camino que usa en RN). Lo restauramos después.
  const desc = Object.getOwnPropertyDescriptor(navigator, "product");
  let spoofed = false;
  try {
    Object.defineProperty(navigator, "product", {
      value: "ReactNative",
      configurable: true,
    });
    spoofed = true;
  } catch {
    /* si el webview no deja redefinirlo, seguimos (peor caso: navega el webview) */
  }
  try {
    const result = await signIn("google", {
      redirectTo: DESKTOP_OAUTH_REDIRECT,
    });
    const redirect =
      result && typeof result === "object" && "redirect" in result
        ? result.redirect
        : undefined;
    if (!redirect) {
      throw new Error("Convex no devolvió la URL de OAuth de Google.");
    }
    await openExternal(redirect.toString());
  } finally {
    if (spoofed) {
      if (desc) Object.defineProperty(navigator, "product", desc);
      else
        try {
          // @ts-expect-error borrar la prop propia que agregamos
          delete navigator.product;
        } catch {
          /* noop */
        }
    }
  }
}

/**
 * Escucha el deep link de vuelta y completa el login. Devuelve un `unlisten`.
 * No-op fuera de desktop.
 */
export async function listenForDesktopOAuthCallback(
  signIn: SignInFn,
  onError?: (e: unknown) => void,
): Promise<() => void> {
  if (!isDesktopApp()) return () => {};

  const completeFromUrl = async (url: string) => {
    try {
      const code = new URL(url).searchParams.get("code");
      if (!code) return;
      await signIn("google", { code });
    } catch (e) {
      onError?.(e);
    }
  };

  const { onOpenUrl, getCurrent } = await import("@tauri-apps/plugin-deep-link");

  // Cold start: la app se abrió DESDE el deep link (típico en Windows/Linux).
  try {
    const initial = await getCurrent();
    if (initial && initial.length > 0) await completeFromUrl(initial[0]);
  } catch {
    /* getCurrent puede no estar disponible en todas las plataformas; ignorar */
  }

  // Warm: la app ya estaba abierta cuando llegó el deep link (macOS nativo;
  // Windows/Linux vía single-instance con feature "deep-link").
  return await onOpenUrl((urls) => {
    if (urls && urls.length > 0) void completeFromUrl(urls[0]);
  });
}
